import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Download, Copy, Check, Clapperboard, Heart, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import CustomCursor from "@/components/CustomCursor";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ytId = (url) => {
  const m = url.match(/(?:youtu\.be\/|shorts\/|embed\/|watch\?v=|v\/)([\w-]{11})/) || url.match(/^([\w-]{11})$/);
  return m ? m[1] : null;
};

const VideoCard = ({ video, index }) => {
  const [copied, setCopied] = useState(false);
  const id = ytId(video.youtube_url);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(video.prompt);
      setCopied(true);
      toast.success("Prompt copied to clipboard.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Could not copy. Use the download instead.");
    }
  };

  const downloadPrompt = () => {
    const blob = new Blob([`${video.title}\n\nPrompt by Nayan Verma — nayanverma.com\n\n${video.prompt}`], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${video.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 60)}-prompt.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Prompt downloaded. Create some magic.");
  };

  return (
    <Reveal delay={(index % 2) * 0.1}>
      <article className="border border-[#121110]/15 rounded-2xl overflow-hidden bg-[#E3DCCD]/60" data-testid={`video-card-${index}`}>
        <div className="relative aspect-video bg-[#121110]">
          {id ? (
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${id}`}
              title={video.title}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
              data-testid={`video-embed-${index}`}
            />
          ) : (
            <a href={video.youtube_url} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex items-center justify-center text-[#EFEAE1]">
              Watch on YouTube
            </a>
          )}
        </div>
        <div className="p-7 lg:p-8">
          <div className="flex items-start justify-between gap-4">
            <h3 className="font-giant text-2xl lg:text-3xl text-[#121110]" data-testid={`video-title-${index}`}>{video.title}</h3>
            <span className="inline-flex items-center gap-1.5 text-sm text-[#4A453F] shrink-0 mt-1">
              <Heart size={15} className="fill-[#A8512E] text-[#A8512E]" /> {video.likes ?? 0}
            </span>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to={`/content/${video.id}`} className="pill pill--solid inline-flex items-center gap-2 text-sm px-6 py-3" data-testid={`video-open-post-${index}`}>
              View post <ArrowUpRight size={15} />
            </Link>
            {video.pdf_name ? (
              <a
                href={`${API}/videos/${video.id}/pdf`}
                className="pill inline-flex items-center gap-2 text-sm px-6 py-3"
                data-testid={`video-pdf-download-${index}`}
              >
                <Download size={15} /> Download PDF
              </a>
            ) : (
              video.prompt && (
                <>
                  <button
                    onClick={downloadPrompt}
                    className="pill inline-flex items-center gap-2 text-sm px-6 py-3"
                    data-testid={`video-download-${index}`}
                  >
                    <Download size={15} /> Download prompt
                  </button>
                  <button
                    onClick={copyPrompt}
                    className="pill inline-flex items-center gap-2 text-sm px-6 py-3"
                    data-testid={`video-copy-${index}`}
                  >
                    {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy prompt"}
                  </button>
                </>
              )
            )}
          </div>
        </div>
      </article>
    </Reveal>
  );
};

export default function ContentPage() {
  const [videos, setVideos] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/videos`).then((r) => setVideos(r.data)).catch(() => setVideos([]));
  }, []);

  return (
    <div className="relative bg-[#EFEAE1] text-[#121110] min-h-screen cursor-hidden">
      <CustomCursor />
      <header className="fixed top-0 left-0 right-0 z-50 px-5 lg:px-10 py-4 lg:py-5 flex items-center justify-between" data-testid="content-navbar">
        <Link to="/" className="pill inline-flex items-center gap-2" data-testid="content-back-home">
          <ArrowLeft size={14} /> nayanverma.com
        </Link>
        <a href="/#contact" className="pill pill--solid" data-testid="content-contact-cta">Contact</a>
      </header>

      <main className="pt-28 lg:pt-36 pb-24" data-testid="content-page">
        <div className="px-5 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}>
            <p className="font-swash text-2xl lg:text-3xl text-[#A8512E]">from my instagram, with the recipe</p>
            <h1 className="font-giant text-[13vw] lg:text-[8.5vw] leading-[0.9] mt-4" data-testid="content-heading">
              AI VID<span className="font-swash text-[#A8512E] text-[11vw] lg:text-[7vw]">e</span>OS
            </h1>
            <p className="max-w-xl text-lg lg:text-xl leading-relaxed text-[#4A453F] mt-8">
              The AI videos I share — with the exact prompts behind them. Watch the result,
              download the prompt, and go make your own magic.
            </p>
          </motion.div>

          {videos === null && <p className="mt-20 text-[#4A453F]">Loading…</p>}

          {videos !== null && videos.length === 0 && (
            <div className="mt-20 border border-[#121110]/15 rounded-2xl p-16 text-center max-w-xl" data-testid="content-empty-state">
              <Clapperboard size={28} className="text-[#A8512E] mx-auto mb-6" />
              <p className="font-swash text-2xl text-[#4A453F]">new drops coming soon — quietly.</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
            {(videos || []).map((v, i) => (
              <VideoCard key={v.id} video={v} index={i} />
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
